import { useId } from "react";
import { SunMedium } from "lucide-react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import type { EnergyFlowPoint } from "@/lib/homey-subviews";
import { EnergyPanel, EnergyPanelDot } from "./EnergyPanel";
import { formatChartValue } from "./chart-format";

interface EnergyFlowChartProps {
  /**
   * Merged energy-flow series in watts, aligned by timestamp. `t` should
   * already be a display label (e.g. "14:00") — labeling is a page concern.
   */
  data: EnergyFlowPoint[];
  /** Direct-use (self-consumed) energy today, kWh. Blue header total. */
  directUseKwh?: number | null;
  /** Energy imported from the grid today, kWh. Purple header total. */
  gridImportKwh?: number | null;
  /** Solar produced today, kWh. Green header total. */
  productionKwh?: number | null;
  /** Self-sufficiency / direct-use percentage (0–100). */
  directUsePct?: number | null;
  /** Title shown beside the solar icon. */
  title?: string;
  unit?: string;
  className?: string;
}

// Series colours map to the HomeWizard "Zelfvoorzienend" reference:
//   blue   = self-sufficient / direct use  -> --domain-climate (cyan-blue)
//   green  = export / teruglevering        -> --domain-energy  (emerald)
//   purple = grid import / net             -> --domain-flow    (violet)
const DIRECT_USE_COLOR = "var(--domain-climate)";
const EXPORT_COLOR = "var(--domain-energy)";
const IMPORT_COLOR = "var(--domain-flow)";

function fmtKwh(v: number | null | undefined): string {
  return typeof v === "number" && Number.isFinite(v) ? v.toFixed(1) : "–";
}

/**
 * Energy-flow chart styled after the HomeWizard "Zelfvoorzienend"
 * (self-sufficiency) view: a dark "hero" panel with a layered, stacked gradient
 * area chart.
 *
 * Three areas share `stackId="flow"` and stack bottom→top:
 *   1. directUse  (blue base)   — solar consumed directly at home
 *   2. gridExport (green)       — surplus solar fed back to the grid
 *   3. gridImport (purple)      — shortfall drawn from the grid
 *
 * Because gridExport and gridImport are mutually exclusive per timestamp (see
 * buildEnergyFlowSeries), exactly one sits atop the blue base at any moment, so
 * the stack's top edge equals max(production, consumption) — exactly the
 * reference's silhouette.
 *
 * Intentionally dark in BOTH app themes: this is a premium dark panel, so it
 * keeps a near-black background and light-on-dark ticks/text regardless of the
 * ambient theme.
 */
export function EnergyFlowChart({
  data,
  directUseKwh,
  gridImportKwh,
  productionKwh,
  directUsePct,
  title = "Self-sufficiency",
  unit = " W",
  className,
}: EnergyFlowChartProps) {
  const uid = useId();
  const directGradient = `flow-direct-${uid}`;
  const exportGradient = `flow-export-${uid}`;
  const importGradient = `flow-import-${uid}`;

  const isEmpty = data.length === 0;

  return (
    <EnergyPanel
      className={className}
      icon={<SunMedium aria-hidden />}
      title={title}
      headerDetail={
        <div className="flex items-baseline gap-2 text-base font-semibold tabular-nums">
          <span style={{ color: DIRECT_USE_COLOR }}>{fmtKwh(directUseKwh)}</span>
          <span style={{ color: IMPORT_COLOR }}>{fmtKwh(gridImportKwh)}</span>
          <span style={{ color: EXPORT_COLOR }}>{fmtKwh(productionKwh)}</span>
          <span className="text-xs font-normal text-zinc-500">kWh</span>
          {typeof directUsePct === "number" && Number.isFinite(directUsePct) && (
            <span className="ml-1 text-xs font-normal text-zinc-400">
              · {Math.round(directUsePct)}% direct
            </span>
          )}
        </div>
      }
      legend={
        <>
          <span className="flex items-center gap-1.5">
            <EnergyPanelDot color={DIRECT_USE_COLOR} /> Self-sufficient
          </span>
          <span className="flex items-center gap-1.5">
            <EnergyPanelDot color={IMPORT_COLOR} /> Grid
          </span>
          <span className="flex items-center gap-1.5">
            <EnergyPanelDot color={EXPORT_COLOR} /> Export
          </span>
        </>
      }
    >
      {isEmpty ? (
        <div className="flex h-56 items-center justify-center">
          <p className="text-sm text-zinc-500">No energy flow history yet.</p>
        </div>
      ) : (
        <div className="h-56 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
              <defs>
                <linearGradient id={directGradient} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={DIRECT_USE_COLOR} stopOpacity={0.85} />
                  <stop offset="100%" stopColor={DIRECT_USE_COLOR} stopOpacity={0.15} />
                </linearGradient>
                <linearGradient id={exportGradient} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={EXPORT_COLOR} stopOpacity={0.85} />
                  <stop offset="100%" stopColor={EXPORT_COLOR} stopOpacity={0.15} />
                </linearGradient>
                <linearGradient id={importGradient} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={IMPORT_COLOR} stopOpacity={0.85} />
                  <stop offset="100%" stopColor={IMPORT_COLOR} stopOpacity={0.15} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="rgba(255,255,255,0.06)" vertical={false} />
              <XAxis
                dataKey="t"
                tickLine={false}
                axisLine={false}
                minTickGap={48}
                tick={{ fontSize: 10, fill: "rgba(255,255,255,0.45)" }}
              />
              <YAxis
                orientation="right"
                width={44}
                tickLine={false}
                axisLine={false}
                tick={{ fontSize: 10, fill: "rgba(255,255,255,0.45)" }}
              />
              <Tooltip
                formatter={(v, name) => [formatChartValue(v, unit), name]}
                contentStyle={{
                  background: "rgb(24 24 27)",
                  border: "1px solid rgba(255,255,255,0.1)",
                  borderRadius: "0.75rem",
                  color: "rgb(244 244 245)",
                  fontSize: 12,
                }}
                labelStyle={{ color: "rgba(255,255,255,0.6)" }}
              />
              {/* Stack bottom -> top: blue base, then green export, then purple
                  import. Export/import are mutually exclusive, so the stack top
                  equals max(production, consumption) (the reference silhouette). */}
              <Area
                type="monotone"
                stackId="flow"
                dataKey="directUse"
                name="Self-sufficient"
                stroke={DIRECT_USE_COLOR}
                strokeWidth={1.5}
                fill={`url(#${directGradient})`}
                isAnimationActive
                animationDuration={400}
              />
              <Area
                type="monotone"
                stackId="flow"
                dataKey="gridExport"
                name="Export"
                stroke={EXPORT_COLOR}
                strokeWidth={1.5}
                fill={`url(#${exportGradient})`}
                isAnimationActive
                animationDuration={400}
              />
              <Area
                type="monotone"
                stackId="flow"
                dataKey="gridImport"
                name="Grid"
                stroke={IMPORT_COLOR}
                strokeWidth={1.5}
                fill={`url(#${importGradient})`}
                isAnimationActive
                animationDuration={400}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}
    </EnergyPanel>
  );
}
